/**
 * THE CONVERSATION BAR — a floating call button that opens into the bar.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS USED TO BE, AND WHY IT IS NOT THAT ANY MORE
 *
 * Until 2026-08-24 this file was a deliberate EXACT PORT of elevenlabs/ui's
 * `conversation-bar.tsx`: an in-flow bar, always at full size, rendered with this
 * repo's Tailwind primitives, reproducing upstream's class list character for
 * character — including upstream's weaknesses, which the old header defended at
 * length. Product decision 2026-08-24 supersedes that requirement. The variant is
 * now a LAUNCHER: a rounded-square call button parked in a corner of the visitor's window
 * that expands sideways into the bar and starts the call in the same click.
 *
 * Three things follow from that decision and none of them are cosmetic:
 *
 *   1. THE BAR IS NO LONGER ALWAYS ON SCREEN. A 280 px control pinned over
 *      somebody's page for the whole visit is the complaint that gets a widget
 *      uninstalled; a 56 px tile is not. The bar exists only for the length of a
 *      call, which is also the only time its contents mean anything — a waveform
 *      with nothing to draw and a mute button that cannot be pressed are two
 *      controls advertising that the widget is inert.
 *   2. THE SIZE AND THE COLOURS ARE CONFIGURATION, not tokens. See below.
 *   3. IT DEPENDS ON NO STYLESHEET, like `flag-button.tsx` and `bars-visualizer.tsx`.
 *      See below. This is the change that makes the widget safe on a page we have
 *      never seen, and it is the reason there is not one class name in this file.
 *
 * WHAT SURVIVED THE PORT, because the look is still the reason to pick this
 * variant, and a second product decision the same day made the list explicit —
 * KEEP THE ORIGINAL STYLING:
 *
 *   - THE TYPOGRAPHY. `FONT_STACK` is Tailwind's default sans, pinned as an inline
 *     style, which is what upstream renders in. It is not the system stack the other
 *     two stylesheet-free widgets use, and the difference is deliberate: those two
 *     are meant to look native on the host page, and this one is meant to look like
 *     the bar it is a port of.
 *   - THE CORNERS. 14 px on the card, 8 px on every button and the waveform tray —
 *     this repo's `rounded-xl` and `rounded-md` at `--radius: 10px`, the two numbers
 *     the class-based version rendered. See `R_CARD` / `R_BUTTON`, which is also
 *     where the one exception (the round launcher) is argued.
 *   - THE WAVEFORM (`live-waveform.tsx`), still an exact port, still attributed.
 *   - THE CONTROL ROW'S PROPORTIONS: a 120 × 40 waveform tray and 36 px icon buttons
 *     inside a 56 px row, which is upstream's geometry to the pixel at the default
 *     size.
 *   - THE KEYBOARD/CHEVRON CROSSFADE, the borderless ring-less textarea, the 32 px
 *     send button in the corner, and the text panel that grows OUT of the bar rather
 *     than pushing it down the page.
 *
 * Attribution is in §9 of `docs/widgets.md` and in the waveform's own header; this
 * is still a derivative work.
 *
 * WHAT WENT WITH THE PORT REQUIREMENT, each one a thing the old header defended as
 * faithfulness and none of which a launcher can keep:
 *
 *   - THE HANG-UP BUTTON MOVED OUT OF THE BAR AND INTO THE LAUNCHER. One control
 *     starts the call and ends it, in the same place, which is what makes the
 *     expand read as one object rather than two. Upstream's separator went with it:
 *     it existed to divide the hang-up from the other two.
 *   - AN ERROR IS SHOWN. Upstream renders `error` pixel-identically to
 *     `disconnected`, so a visitor with a blocked microphone gets a control that
 *     appears to do nothing, forever. That was reproducible while the bar was
 *     always visible and is indefensible for a launcher: the widget would collapse
 *     back to a button and the click would look ignored. The message is
 *     `friendlyError`'s, already written for a visitor.
 *   - `caption` AND `theme` ARE NOT READ, and are no longer offered for this
 *     variant in the config editor. There is nowhere in a 56 px tile for a
 *     "powered by" line that would not be a second floating object on the page, and
 *     a light/dark switch is a control that lies once the widget paints its own
 *     surface: `surface`, `onSurface` and `surfaceBorder` ARE its theme.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY COLOUR AND DIMENSION HERE IS AN INLINE STYLE
 *
 * There is not one Tailwind class, one `cn()` call, one `ui/` primitive and one
 * icon-package import in this file, which looks wrong next to the rest of `web/`
 * and is the point. This component is bundled into `embed.js` and mounted inside a
 * closed shadow root on a page we do not control. It used this repo's primitives
 * until 2026-08-24, and every one of them resolves a custom property that is
 * declared on `:root` — which DOES NOT CROSS A SHADOW BOUNDARY. Inside the embed it
 * only ever looked right because `embed.css` re-declares those tokens on `:host`,
 * so the widget was one failed stylesheet away from a transparent card with
 * inherited text on it: `bg-card` with no `--card` is not white, it is nothing.
 *
 * So everything load-bearing is an inline style and this file depends on no CSS
 * whatsoever. That includes five things that are easy to forget:
 *
 *   - `boxSizing: 'border-box'` on everything with a width, since there is no reset
 *     to rely on and a host page's `* { box-sizing: content-box }` is a real thing;
 *   - `fontFamily: FONT_STACK` and the inherited typography properties
 *     (`fontWeight`, `letterSpacing`, `textTransform`, `lineHeight`) restated on the
 *     root, because a host page's `body { text-transform: uppercase }` crosses into
 *     a component that does not set them;
 *   - SIZES IN px, NEVER rem. `rem` resolves against the HOST page's `<html>`
 *     font-size even inside a shadow root, and pages that set it to 10px exist;
 *   - the focus rings, which cannot be `:focus-visible` rules with no stylesheet
 *     and are instead a two-tone `box-shadow` toggled from a
 *     `matches(':focus-visible')` check in `onFocus` — two tones because we do not
 *     know whether the page behind the ring is white or black;
 *   - the narrow-screen switch and `prefers-reduced-motion`, which are `matchMedia`
 *     listeners rather than an `sm:` variant or a media query, for the same reason:
 *     a compiled breakpoint lives in a stylesheet and there is none.
 *
 * THE ONE TINT TRICK WORTH NAMING. Upstream's subtle surfaces are `bg-foreground/5`
 * and `bg-foreground/10` — an alpha of a token. There is no token here and
 * `onSurface` is an arbitrary CSS colour, so an alpha of it cannot be computed
 * without `color-mix` (which a browser may not have, silently dropping the
 * declaration). Instead every tint is a positioned `<span>` filled with
 * `currentColor` at a low `opacity`, which is exact, works in every engine, and
 * follows whatever colour the operator set.
 *
 * ---------------------------------------------------------------------------
 * ONE NUMBER CONFIGURES THE GEOMETRY, AND IT IS THE ROW HEIGHT
 *
 * `size` is the launcher's SIDE AND the expanded bar's height, and every other
 * dimension is the reference constant times `size / 56`. 56 is not arbitrary: it is
 * upstream's row — 8 px of padding, a 40 px waveform tray, 8 px of padding — so
 * `size = 56` renders upstream's proportions exactly and anything else is a clean
 * scale of them. A widget on a customer's page needs to be able to get bigger
 * without a second config field for every part of it.
 *
 * ---------------------------------------------------------------------------
 * WHICH WAY IT OPENS IS DERIVED, NOT CONFIGURED
 *
 * A launcher in a right-hand corner opens LEFT and one in a left-hand corner opens
 * RIGHT, because the alternative is a panel that grows off the side of the window.
 * There is no `side` field to get wrong: `placement` already says which corner, and
 * the corner already implies the only direction that fits. `top-*` placements grow
 * the text panel DOWNWARD for the same reason.
 *
 * `inline` IS NOT A PLACEMENT FOR THIS VARIANT. A launcher is defined by floating
 * over the page — "a corner-parked call button, in this div" is not a thing — so
 * `inlineSnippetFor` refuses to generate an inline snippet for `bar` (the rule the
 * flag has always followed), and an `inline` reaching this component is read as
 * `bottom-right` rather than rendered in flow.
 *
 * ---------------------------------------------------------------------------
 * THE WAVEFORM MAPPING, unchanged from the port, because it is still upstream's:
 *
 *   our phase                    upstream mode
 *   ---------------------------  ------------------------------------------------
 *   idle, ended, ending          idle — dotted 2 px centre line, canvas faded out
 *   connecting, thinking         `processing` — three summed sines, centre-weighted
 *   listening, speaking          `active` — upstream's `static` bars: a mirrored
 *                                FFT spectrum in fixed slots, redrawn in place
 *   error                        idle, behind the message
 *
 * Upstream's full REMOUNT of the waveform across the disconnected boundary
 * (`key={…}`) is kept: it is what discards a finished call's history so the next
 * call starts on an empty canvas.
 *
 * ---------------------------------------------------------------------------
 * WHAT COULD NOT BE PORTED, and still cannot. Upstream sends a contextual update to
 * the agent on every keystroke, so the model can see what you are typing before you
 * send it. There is no message for that on our wire and inventing one would mean a
 * per-keystroke round trip. Typing is silent here; sending sends.
 */
// `KeyboardEvent` shadows the DOM global for this module, which is what we want:
// every keyboard event in here is React's synthetic one.
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { LiveWaveform, type LiveWaveformProps } from './live-waveform'
import { isLive } from './types'
import type { VoiceControl, VoicePhase, VoicePlacement, VoiceWidgetCommon } from './types'
import { withTakeover } from './stage-text'

/**
 * `caption` and `theme` are omitted rather than accepted-and-ignored, so the
 * refusal is in the type where a caller reads it. See the header.
 */
export interface ConversationBarProps extends Omit<VoiceWidgetCommon, 'caption' | 'theme'> {
  control: VoiceControl
  /**
   * Which corner the launcher parks in. `left`/`right` are read as the bottom
   * corners and `inline` as `bottom-right` — a launcher has no in-flow form.
   */
  placement?: VoicePlacement
  /** The launcher's side and the expanded bar's height, in px. Everything else
   *  scales from it. Default 56, which is upstream's row height. */
  size?: number
  /** The expanded bar's surface. Default white. */
  surface?: string
  /** Text, icons and waveform bars ON the surface. Default near-black. */
  onSurface?: string
  /** The surface's hairline. Explicit rather than derived, because an alpha of an
   *  arbitrary CSS colour cannot be computed here — see the header. */
  surfaceBorder?: string
  /** Show the text input affordance at all. Default true. */
  allowText?: boolean
  /** Show the mute toggle. Default true. */
  allowMute?: boolean
  zIndex?: number
}

/**
 * TAILWIND'S DEFAULT `--font-sans`, which is what upstream renders in — the port's
 * own pin, kept (product decision 2026-08-24: keep the original typography).
 *
 * "Exact typography" cannot be inherited. The panel sets `--font-sans` to Geist, and
 * inside the embed's shadow root `:host { font-family: inherit }` means the widget
 * would pick up the HOST PAGE's font — verified once: a customer page with a serif
 * body rendered a serif bar. So the stack is stated here as an inline style, which
 * is the only declaration a host page cannot defeat without `!important`, and it
 * holds with no stylesheet at all.
 *
 * The emoji families are part of the stack rather than decoration: without them a
 * host page that has set an emoji fallback of its own would still reach it.
 */
const FONT_STACK =
  'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'

/**
 * THE REFERENCE ROW HEIGHT, and the divisor for every other number here.
 *
 * Upstream's control row is `p-2` (8) + a `md:h-10` waveform tray (40) + `p-2` (8)
 * = 56. Writing the geometry against it means `size = 56` is upstream's bar to the
 * pixel and every other size is a clean scale of it.
 */
const REF = 56

/** Upstream's row padding, waveform tray, and icon buttons — all at REF. */
const PAD = 8
const WAVE_W = 120
const WAVE_H = 40
const BTN = 36
/**
 * THE PHONE GLYPH ON THE LAUNCHER, at REF.
 *
 * It was 23, which is 41% of the button — a number that came from upstream's 20 px
 * icons in a 36 px control row and does not transfer: those sit in a row of three,
 * and this is the only thing inside a 56 px tile that a visitor has to recognise
 * from across a page. 26 is 46%, still short of the ~50% where an icon starts to
 * feel crammed against its own corners, and it is the size the button reads as a
 * PHONE rather than as a small mark in a yellow square.
 */
const GLYPH = 26
/** How far the handset tilts under the pointer. See `PhoneGlyph`. */
const TILT_DEG = 12
/** Between the launcher and the bar, and between the tray and the controls. */
const GAP = 8
/**
 * Between the two icon buttons, and SMALLER than `GAP` on purpose.
 *
 * Upstream had a separator here; it went with the hang-up when that moved into the
 * launcher (see the header), and the two remaining buttons were left flush against
 * each other, reading as one two-cell object rather than two controls. A gap the
 * same size as the one before the tray would fix the touching and lose the
 * grouping — 6 against 8 says "these two belong together, and together they are
 * separate from the waveform". The bar grows by exactly this, which is the
 * honest way to pay for it: nothing else moves.
 */
const CTRL_GAP = 6
/** Upstream's `max-h-[120px]` panel around a `min-h-[100px]` textarea, its
 *  `px-2.5 py-2` and `pr-12`, and the 32 px send button at `right-3 bottom-3`. */
const TEXT_MAX_H = 120
const TEXTAREA_MIN_H = 100
const TEXTAREA_PAD_Y = 8
const TEXTAREA_PAD_X = 10
const TEXTAREA_PAD_RIGHT = 48
const SEND = 32
const SEND_INSET = 12

/**
 * THE CORNERS ARE THE PORT'S CORNERS (product decision 2026-08-24: keep the
 * original styling). This repo's radius scale is `--radius: 10px` with
 * `--radius-md: calc(var(--radius) * 0.8)` and `--radius-xl:
 * calc(var(--radius) * 1.4)`, so the two numbers the old bar rendered were:
 *
 *   14 px  the `Card` (`rounded-xl`) that IS the bar's surface
 *    8 px  every button and the waveform tray (`rounded-md`, named explicitly in
 *          the port so it landed on upstream's corner rather than this repo's
 *          10 px `rounded-lg` default)
 *
 * They are literals here rather than `var(--radius-*)` because a custom property
 * declared on `:root` does not cross a shadow boundary — the whole reason this file
 * has no classes in it. Same numbers, one less thing that can fail to arrive.
 *
 * THE LAUNCHER USED TO BE A DISC, and the argument for it was that a circle beside a
 * 14 px-cornered card is the shape a person already reads as "press this". Product
 * decision 2026-08-24, against a reference: it is a rounded square, on the card's own
 * radius. The argument that replaces it is the assembly rather than the button —
 * collapsed, the launcher is the only thing on the page, and a disc reads as a
 * generic chat bubble; open, it sits a `GAP` away from the bar, and two tiles with
 * one corner between them read as one object where a circle and a card read as two.
 */
const R_CARD = 14
const R_BUTTON = 8
/** The error state is wider because a sentence has to fit in it. */
const ERROR_W = 250
/** The gutter between the assembly and the viewport edges. Upstream's `p-4`. */
const MARGIN = 16

/**
 * The stand-in for framer-motion's `{ type: 'spring', stiffness: 300, damping: 30 }`
 * — the flag's curve, for the same reason and with the same argument: that spring is
 * underdamped, overshoots by a couple of per cent and settles at about 400 ms, and a
 * cubic-bezier whose third control point sits above 1 does the same for none of the
 * 30 kB.
 */
const SPRING = 'cubic-bezier(0.34, 1.4, 0.64, 1)'
const SPRING_MS = 400
/** Upstream's `duration-300` fades, and its `duration-200` icon crossfade. */
const FADE_MS = 300
const ICON_MS = 200

/** Below this the expanded bar spans the window instead of hanging off one corner.
 *  480 px is the widest phone in portrait; a small tablet keeps the corner form. */
const NARROW_MAX_PX = 480

/**
 * Very nearly the top of the int32 stack, with ~600 left above it.
 *
 * This renders on other people's websites, over cookie banners, sticky headers and
 * chat widgets that all picked 9999 and thought they had won. The headroom is
 * deliberate: it leaves room for our own future overlay to sit above the widget
 * without another round of escalation, and a host who genuinely needs their modal on
 * top can pass a lower value rather than fighting us.
 */
const DEFAULT_Z_INDEX = 2_147_483_000

/**
 * The component's own defaults. A seed in the config editor must not be the only
 * place these exist, or a hand-written script tag renders something nobody chose —
 * and since 2026-08-25 THERE IS NO SEED FOR THE SECOND ONE: the editor stopped
 * offering an end label for this variant, because the launcher is an icon and the
 * string only ever reached a tooltip and a screen reader, which is the job `label`
 * was already doing before the call started. This default is now the only value most
 * installs will ever use; a tag carrying `data-end-label` still overrides it.
 */
const DEFAULT_LABEL = 'Talk to us'
const DEFAULT_END_LABEL = 'End call'
/**
 * THE CALL BUTTON'S OWN LIVERY, AND IT IS THE FLAG'S (product decision 2026-08-25).
 *
 * This pair has been three things. It shipped a saturated blue — a colour picked for
 * the customer, on a page whose palette we have never seen. It was made neutral on
 * 2026-08-24, light grey on near-black, on the argument that the quiet option is the
 * better default for the only object this widget puts on somebody's page for the
 * whole of a visit. The grey lost, for a reason that only shows up on a real page:
 * quiet and invisible are the same thing on a white one, and a pale tile held up by
 * nothing but a soft shadow is not a thing a visitor reads as a button.
 *
 * So it is `flag-button.tsx`'s yellow with black on it, to the hex, and that is
 * deliberately a SHARED default rather than a coincidence. The flag and this are the
 * same product — a call control parked at the edge of a page we do not own — and an
 * operator who installs one of each should not get two house colours by accident.
 * Yellow on black also settles the contrast question for dark pages as well as light
 * ones, which the grey pair only managed in one direction.
 *
 * A widget already installed with `data-accent` on its tag is unaffected; one
 * without it changes on the next page load, which is the point of a default.
 */
const ACCENT = '#fac800'
const ON_ACCENT = '#000000'
const SURFACE = '#ffffff'
const ON_SURFACE = '#0a0a0a'
const SURFACE_BORDER = 'rgba(0, 0, 0, 0.12)'

/** Opacities for the `currentColor` tints described in the header. */
const TRAY_TINT = 0.05
const PRESSED_TINT = 0.1

type Corner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'

/**
 * Every `VoicePlacement` read as one of the four corners.
 *
 * `left` and `right` exist for the flag's vertical tab and are read as the BOTTOM
 * corners rather than as a launcher pinned to the middle of a screen edge, which
 * would look like a mistake. `inline` is not a launcher at all — see the header —
 * and lands on the default rather than rendering in flow.
 */
function cornerOf(placement: VoicePlacement): Corner {
  switch (placement) {
    case 'left':
    case 'bottom-left':
      return 'bottom-left'
    case 'top-left':
      return 'top-left'
    case 'top-right':
      return 'top-right'
    default:
      return 'bottom-right'
  }
}

/**
 * What each phase is called, for the live region. Since the bar draws `listening`
 * and `speaking` identically, this is the only place in the component where the
 * difference between them exists, and a screen reader is the only user who gets it.
 */
const PHASE_TEXT: Record<VoicePhase, string> = {
  idle: 'Not connected',
  connecting: 'Connecting',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'The assistant is speaking',
  ended: 'Call ended',
  error: 'The call could not be connected',
}

/**
 * `VoicePhase` collapsed onto the three things upstream can draw. See the mapping
 * table in the header.
 *
 * Muting during the visitor's turn falls back to `idle`, which is upstream's
 * `active={isConnected && !isMuted}` — the bars would otherwise sit flat at the
 * floor and look broken rather than muted. Muting during the AGENT's turn does the
 * same, because upstream's boolean does not know there is an agent turn.
 */
function waveformMode(phase: VoicePhase, muted: boolean): LiveWaveformProps['mode'] {
  switch (phase) {
    case 'connecting':
    case 'thinking':
      return 'processing'
    case 'listening':
    case 'speaking':
      return muted ? 'idle' : 'active'
    default:
      return 'idle'
  }
}

/** The launcher's name says what a click will DO, which is not the same as what the
 *  widget is currently doing — that is the live region's job. */
function launcherLabel(phase: VoicePhase, label: string, endLabel: string): string {
  if (isLive(phase)) return endLabel
  if (phase === 'connecting') return 'Connecting, please wait'
  if (phase === 'error') return `${label} — retry`
  return label
}

export function ConversationBar({
  control,
  placement = 'bottom-right',
  size = REF,
  label = DEFAULT_LABEL,
  endLabel = DEFAULT_END_LABEL,
  accent = ACCENT,
  onAccent = ON_ACCENT,
  surface = SURFACE,
  onSurface = ON_SURFACE,
  surfaceBorder = SURFACE_BORDER,
  allowText = true,
  allowMute = true,
  zIndex = DEFAULT_Z_INDEX,
}: ConversationBarProps) {
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const [text, setText] = useState('')
  const [ring, setRing] = useState(false)
  const [hover, setHover] = useState(false)
  const [pressed, setPressed] = useState(false)
  /**
   * True once the panel's reveal has FINISHED opening — and it exists to un-clip a
   * shadow.
   *
   * The panel opens by animating `max-height` inside an `overflow: hidden` box, and
   * that box is exactly the panel's width and the panel's height plus its gutter.
   * So it clipped the card's own drop shadow flush at all four edges: the artefact
   * in the reported screenshot was not a bad shadow, it was a good one sliced into
   * a rectangle. The clip is only needed WHILE the height is moving; once the panel
   * is open and still, `overflow: visible` lets the shadow finish, and closing
   * clips again on the same tick the height starts shrinking.
   */
  const [panelSettled, setPanelSettled] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  /**
   * "Sent." for a second and a half, and it is a real gap being closed rather than
   * decoration.
   *
   * THIS VARIANT HAS NOWHERE TO SHOW A TRANSCRIPT — it is a waveform and two icon
   * buttons — so pressing Enter cleared the box and did nothing else visible. The
   * message went; `control.sendText` is the same call the chat flag makes and the
   * same one `CallSession` echoes into its own transcript. But a composer that
   * empties itself and gives no other sign is indistinguishable from a composer
   * that dropped what you typed, and it was read as exactly that.
   *
   * A tick rather than a boolean, so two sends in quick succession restart the
   * timer instead of the second one being swallowed by the first one's timeout.
   */
  const [sentTick, setSentTick] = useState(0)
  const [sent, setSent] = useState(false)

  const still = useMediaQuery('(prefers-reduced-motion: reduce)')
  const narrow = useMediaQuery(`(max-width: ${NARROW_MAX_PX}px)`)

  const { phase, muted } = control
  const live = isLive(phase)
  /**
   * There is no `ending` in either of these, and the absence is the 2026-08-24
   * optimistic hang-up (see `VoicePhase`): pressing the launcher while a call is up
   * shows `ended` immediately, so the bar collapses on the press rather than sitting
   * open through a teardown nobody can act on. A press during that drain is a
   * legitimate "start another one" and the adapter queues it behind the teardown.
   */
  const busy = live || phase === 'connecting'
  /** Connecting is the only stretch a click must wait through. */
  const waiting = phase === 'connecting'
  const failed = phase === 'error'
  const mode = waveformMode(phase, muted)
  /**
   * Upstream's `status === 'disconnected' || status === 'error'`, which drives the
   * canvas fading out and the remount key. (Upstream drives a third thing with it —
   * an idle label in the tray — which this variant does not have; see
   * `launcherLabel`.) Deliberately NOT the same test as `mode`, because upstream is
   * not either: a muted call is `active={false}` — dotted line, cleared canvas —
   * while still being connected, and the canvas stays opaque.
   */
  const disconnected = !live && phase !== 'connecting'

  /**
   * Open for everything except the two resting states — the flag's rule, and for
   * the flag's reason. The two most uncertain seconds of the interaction are the
   * ones after the click and before the call connects; a launcher that waits for
   * `connect()` to resolve looks, for that whole stretch, exactly like a launcher
   * that ignored the click.
   *
   * `ended` collapses, including when the server ended it rather than the visitor:
   * the button is the resting state, and a spent bar sitting open is something the
   * visitor has to dismiss.
   */
  const expanded = phase !== 'idle' && phase !== 'ended'
  /** Expanded, on a phone: one bar across the window instead of a corner assembly. */
  const spanning = narrow && expanded

  const corner = cornerOf(placement)
  const side = corner === 'bottom-right' || corner === 'top-right' ? 'right' : 'left'
  const atTop = corner === 'top-right' || corner === 'top-left'

  /** Every reference constant, scaled. See the header: one number, everything else
   *  derived, sizes in px and never rem. */
  const k = size / REF
  const px = (n: number): number => Math.round(n * k)

  // A call that ends while the panel is open would otherwise leave a disabled
  // textarea hanging above a collapsed button.
  useEffect(() => {
    if (!live) setKeyboardOpen(false)
  }, [live])

  /**
   * Closing re-clips on the same tick, so the shadow cannot spill out of a box that
   * is shrinking. Opening under `prefers-reduced-motion` settles immediately, since
   * there is no transition to wait for and therefore no `transitionend` coming.
   */
  useEffect(() => {
    if (!keyboardOpen) setPanelSettled(false)
    else if (still) setPanelSettled(true)
  }, [keyboardOpen, still])

  // Opening the panel is an explicit click, so taking focus is what was asked for
  // rather than focus theft — and without it the visitor has to click twice.
  useEffect(() => {
    if (keyboardOpen) textareaRef.current?.focus()
  }, [keyboardOpen])

  useEffect(() => {
    if (sentTick === 0) return
    setSent(true)
    const id = setTimeout(() => setSent(false), 1500)
    return () => clearTimeout(id)
  }, [sentTick])

  const send = useCallback(() => {
    const body = text.trim()
    if (!body) return
    control.sendText(body)
    setText('')
    setSentTick((n) => n + 1)
  }, [control, text])

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // `isComposing` is the IME guard: pressing Enter to accept a candidate in
      // Japanese or Chinese must not send the half-finished line. Its absence was
      // the one real defect in this path — everything else about the send works.
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault()
        send()
      }
    },
    [send],
  )

  /**
   * One control starts the call and ends it. Guarded rather than `disabled` while
   * waiting, for the flag's reason: a dimmed hang-up button is worse than an inert
   * one, and the cost — a connect that hangs cannot be cancelled from here until it
   * fails on its own — is the recoverable half of that trade.
   */
  const onClick = useCallback(() => {
    if (waiting) return
    if (busy) control.stop()
    else control.start()
  }, [busy, control, waiting])

  // --- geometry -------------------------------------------------------------

  /** The bar's own width when open. Fixed rather than `auto` so the expand can be a
   *  width transition, which composites; `auto` cannot be animated at all. */
  const controlsW =
    (allowMute ? px(BTN) : 0)
    + (allowText ? px(BTN) : 0)
    + (allowMute && allowText ? px(CTRL_GAP) : 0)
  const barW = failed
    ? px(ERROR_W)
    : px(PAD) + px(WAVE_W) + (controlsW > 0 ? px(GAP) + controlsW : 0) + px(PAD)

  /**
   * THREE LAYERS, ALL FAINT, and it replaces one heavy one.
   *
   * The old shadow was `0 6px 24px rgba(0,0,0,0.18)` over a 1 px contact line, and
   * a single dark layer is exactly what stacked objects cannot afford: with the
   * message panel open, the panel's shadow landed on the bar 8 px below it and the
   * two read as a grey slab between them rather than as two cards. Spreading the
   * same lift over a contact line, a short mid layer and a wide soft one gets the
   * separation from geometry instead of from opacity — nothing here is darker than
   * 10%, so an overlap adds up to a tint rather than to a band.
   */
  const shadow = [
    `0 1px ${px(2)}px rgba(0, 0, 0, 0.05)`,
    `0 ${px(4)}px ${px(10)}px rgba(0, 0, 0, 0.06)`,
    `0 ${px(12)}px ${px(28)}px rgba(0, 0, 0, 0.10)`,
  ].join(', ')
  /** Two tones, so the ring is visible whether the page behind it is light or dark.
   *  Appended to the drop shadow because a box can only have one of these. */
  const ringShadow = ring ? `, 0 0 0 2px ${onAccent}, 0 0 0 5px ${accent}` : ''

  /**
   * Inherited typography restated, because a host page's `body` rules cross into a
   * component that does not set them — see the header. `pointerEvents: 'none'` with
   * `auto` on the real controls is what stops the transparent gutter between the
   * launcher and the bar from swallowing clicks on the page underneath.
   */
  const container: CSSProperties = {
    position: 'fixed',
    ...(spanning
      ? { left: MARGIN, right: MARGIN }
      : side === 'right'
        ? { right: MARGIN }
        : { left: MARGIN }),
    ...(atTop ? { top: MARGIN } : { bottom: MARGIN }),
    zIndex,
    boxSizing: 'border-box',
    display: 'flex',
    // The children are written [row, text panel] and the reverse is what puts the
    // panel ABOVE the row for a bottom-corner launcher, so it grows up out of the
    // bar rather than off the bottom of the window. A top-corner launcher grows it
    // downward instead, which is the same rule and the opposite direction.
    flexDirection: atTop ? 'column' : 'column-reverse',
    alignItems: spanning ? 'stretch' : side === 'right' ? 'flex-end' : 'flex-start',
    pointerEvents: 'none',
    fontFamily: FONT_STACK,
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: `${px(14)}px`,
    lineHeight: 1,
    letterSpacing: 'normal',
    textTransform: 'none',
    textAlign: 'left',
    color: onSurface,
  }

  const row: CSSProperties = {
    display: 'flex',
    flexDirection: side === 'right' ? 'row-reverse' : 'row',
    alignItems: 'center',
    gap: px(GAP),
    ...(spanning ? { width: '100%' } : {}),
  }

  const launcher: CSSProperties = {
    // No reset exists inside the shadow root, so everything a UA stylesheet would
    // have opinions about is stated.
    boxSizing: 'border-box',
    appearance: 'none',
    border: 0,
    margin: 0,
    padding: 0,
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    // The card's corner, not a circle — see the radius note above.
    borderRadius: px(R_CARD),
    background: accent,
    color: onAccent,
    boxShadow: shadow + ringShadow,
    cursor: waiting ? 'progress' : 'pointer',
    outline: 'none',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    pointerEvents: 'auto',
    // A cheap stand-in for the `:hover` rule we cannot write. Skipped while waiting
    // so a button that cannot be clicked does not respond to the pointer.
    filter: hover && !waiting ? 'brightness(0.94)' : 'none',
    // A press that moves nothing reads as a press that did nothing, and this is the
    // one control that both starts and ends the call. Scale rather than a shift, so
    // the button cannot walk out from under the finger, and it is skipped for
    // reduced motion along with everything else animated here.
    transform: pressed && !waiting && !still ? 'scale(0.96)' : 'none',
    transformOrigin: 'center',
    transition: still
      ? 'none'
      : `filter 150ms ease, transform 120ms ease, box-shadow ${FADE_MS}ms ease`,
  }

  /**
   * The expand. `width` and `opacity` transition together; `visibility` is IN the
   * transition list and that is the whole trick — it is a discrete property, so it
   * flips at the START of the transition when becoming visible and at the END when
   * becoming hidden. The fade still plays, and once it has played the collapsed bar
   * stops being painted at all, which is what keeps the waveform's canvas from
   * compositing behind a resting button on every page this is installed on.
   */
  const barSurface: CSSProperties = {
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
    ...(spanning
      ? { flex: '1 1 auto', width: 'auto' }
      : { flex: '0 0 auto', width: expanded ? barW : 0 }),
    minWidth: 0,
    height: size,
    borderRadius: px(R_CARD),
    border: expanded ? `1px solid ${surfaceBorder}` : '0 solid transparent',
    background: surface,
    color: onSurface,
    boxShadow: expanded ? shadow : 'none',
    opacity: expanded ? 1 : 0,
    visibility: expanded ? 'visible' : 'hidden',
    pointerEvents: expanded ? 'auto' : 'none',
    transition: still
      ? 'none'
      : [
          `width ${SPRING_MS}ms ${SPRING}`,
          `opacity ${FADE_MS}ms ease`,
          `visibility ${FADE_MS}ms ease`,
          `box-shadow ${FADE_MS}ms ease`,
        ].join(', '),
  }

  /** Fixed rather than fluid so the contents do not reflow during the width
   *  transition — a row that re-wraps sixty times on the way open reads as a glitch
   *  rather than as a movement. */
  const barInner: CSSProperties = {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: px(GAP),
    width: spanning ? '100%' : barW,
    height: '100%',
    padding: `0 ${px(PAD)}px`,
  }

  return (
    <div style={container}>
      <div style={row}>
        <button
          type="button"
          onClick={onClick}
          aria-label={launcherLabel(phase, label, endLabel)}
          aria-disabled={waiting || undefined}
          // A native tooltip, which is the only kind available with no stylesheet.
          title={launcherLabel(phase, label, endLabel)}
          // `:focus-visible` needs a stylesheet; asking the element whether it
          // matches does not, and gives the same answer.
          onFocus={(e) => setRing(e.currentTarget.matches(':focus-visible'))}
          onBlur={() => setRing(false)}
          onPointerEnter={() => setHover(true)}
          onPointerLeave={() => {
            setHover(false)
            setPressed(false)
          }}
          onPointerDown={() => setPressed(true)}
          onPointerUp={() => setPressed(false)}
          onPointerCancel={() => setPressed(false)}
          style={launcher}
        >
          {waiting ? (
            <Spinner size={px(22)} still={still} />
          ) : busy ? (
            <StopGlyph size={px(20)} />
          ) : (
            <PhoneGlyph size={px(GLYPH)} tilt={hover} still={still} />
          )}
        </button>

        {/* `inert` WHILE COLLAPSED. A zero-width container still holds its contents
            in the tab order and the accessibility tree, so without it a resting
            button leaves two phantom tab stops and a screen-reader-readable
            waveform in the middle of somebody's page. Zero rendered difference. */}
        <div style={barSurface} inert={!expanded}>
          <div style={barInner}>
            {failed ? (
              <ErrorBody message={control.error} px={px} />
            ) : (
              <>
                {/* THE TRAY. Upstream's `bg-foreground/5` behind the waveform, as a
                    `currentColor` span rather than an alpha of a token — see the
                    header. */}
                <div
                  style={{
                    position: 'relative',
                    flex: '1 1 auto',
                    minWidth: 0,
                    width: px(WAVE_W),
                    height: px(WAVE_H),
                    borderRadius: px(R_BUTTON),
                    overflow: 'hidden',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'currentColor',
                      opacity: TRAY_TINT,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      opacity: disconnected ? 0 : 1,
                      transition: still ? 'none' : `opacity ${FADE_MS}ms ease`,
                    }}
                  >
                    <LiveWaveform
                      // Upstream's remount across the disconnected boundary,
                      // reproduced. A new call starts on an empty canvas.
                      key={disconnected ? 'idle' : 'active'}
                      levels={control.levels}
                      // Undefined for a hand-made control; the waveform falls back
                      // to `levels` and says so in its own header.
                      spectrum={control.spectrum}
                      mode={mode}
                      height={px(WAVE_H)}
                      barWidth={Math.max(1, px(3))}
                      barGap={Math.max(1, px(1))}
                      // Upstream's `sensitivity`, applied to every bin.
                      sensitivity={1.8}
                      // Upstream's value. On a 3 px bar the browser scales the
                      // radius down to 1.5 — fully rounded ends — so this is 4
                      // because upstream says 4, not because 4 is visible.
                      barRadius={4}
                      // Stated rather than inherited: the canvas would otherwise
                      // resolve its colour from `getComputedStyle`, which is one
                      // more thing that depends on the cascade reaching us.
                      barColor={onSurface}
                      fadeEdges
                      fadeWidth={px(24)}
                    />
                  </div>
                </div>

                {(allowMute || allowText) && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: px(CTRL_GAP),
                      flex: '0 0 auto',
                    }}
                  >
                    {allowMute && (
                      <IconButton
                        px={px}
                        still={still}
                        pressed={muted}
                        disabled={!live}
                        accent={accent}
                        onSurface={onSurface}
                        onClick={control.toggleMute}
                        label={muted ? 'Unmute your microphone' : 'Mute your microphone'}
                      >
                        {muted ? <MicOffGlyph size={px(18)} /> : <MicGlyph size={px(18)} />}
                      </IconButton>
                    )}
                    {allowText && (
                      <IconButton
                        px={px}
                        still={still}
                        pressed={keyboardOpen}
                        disabled={!live}
                        accent={accent}
                        onSurface={onSurface}
                        onClick={() => setKeyboardOpen((v) => !v)}
                        label={keyboardOpen ? 'Close the message box' : 'Type a message'}
                      >
                        {/* THE ONE PIECE OF REAL CRAFT IN UPSTREAM'S COMPONENT,
                            reproduced. Two glyphs occupy the same box and trade
                            places: the outgoing one shrinks away on an ease-out
                            quintic, the incoming one arrives 50 ms later on an
                            overshooting cubic, so the swap has a small bounce at
                            the end instead of a stop. Both transform-only, because
                            a crossfade should never touch layout. */}
                        <span
                          style={{
                            position: 'relative',
                            display: 'block',
                            width: px(18),
                            height: px(18),
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              inset: 0,
                              transform: keyboardOpen ? 'scale(0.75)' : 'scale(1)',
                              opacity: keyboardOpen ? 0 : 1,
                              transition: still
                                ? 'none'
                                : `transform ${ICON_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${ICON_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                            }}
                          >
                            <KeyboardGlyph size={px(18)} />
                          </span>
                          <span
                            style={{
                              position: 'absolute',
                              inset: 0,
                              transform: keyboardOpen ? 'scale(1)' : 'scale(0.75)',
                              opacity: keyboardOpen ? 1 : 0,
                              transition: still
                                ? 'none'
                                : `transform ${ICON_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1) 50ms, opacity ${ICON_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1) 50ms`,
                            }}
                          >
                            <ChevronDownGlyph size={px(18)} />
                          </span>
                        </span>
                      </IconButton>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* THE TEXT PANEL, written after the row and placed before it by the
          container's `column-reverse` — upstream's `flex-col-reverse` trick, which
          is what makes it grow OUT of the bar instead of pushing the bar down the
          page. The gutter between the two lives INSIDE this clipped box, as a
          margin on the card, so a closed panel contributes no height at all and the
          bar does not jump 8 px when it opens. */}
      <div
        inert={!keyboardOpen}
        // `max-height` is the only property in this transition, so this cannot be
        // woken by the opacity or transform of anything inside it.
        onTransitionEnd={(e) => {
          if (e.propertyName === 'max-height' && keyboardOpen) setPanelSettled(true)
        }}
        style={{
          boxSizing: 'border-box',
          // Clipped only while the height is moving — see `panelSettled`.
          overflow: panelSettled ? 'visible' : 'hidden',
          maxHeight: keyboardOpen ? px(TEXT_MAX_H) + px(GAP) : 0,
          // ALIGNED TO THE BAR, NOT TO THE ASSEMBLY. The row is bar + gutter +
          // launcher, so a panel that simply matched the container's edge sat over
          // the launcher and out past it by exactly the launcher's width. The
          // margin on the launcher's side is what lines its edge up with the bar's;
          // it is also what makes `stretch` produce the bar's width on a phone.
          ...(side === 'right'
            ? { marginRight: size + px(GAP) }
            : { marginLeft: size + px(GAP) }),
          ...(spanning ? { width: 'auto' } : { width: barW }),
          pointerEvents: keyboardOpen ? 'auto' : 'none',
          transition: still ? 'none' : `max-height ${FADE_MS}ms ease-out`,
        }}
      >
        <div
          style={{
            boxSizing: 'border-box',
            position: 'relative',
            ...(atTop ? { marginTop: px(GAP) } : { marginBottom: px(GAP) }),
            padding: px(PAD),
            borderRadius: px(R_CARD),
            border: `1px solid ${surfaceBorder}`,
            background: surface,
            color: onSurface,
            boxShadow: shadow,
          }}
        >
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            // NO `placeholder` ATTRIBUTE, and the reason is a real defect rather
            // than a preference: `::placeholder` is a pseudo-element, a pseudo-
            // element needs a stylesheet, and this file has none — so the UA's grey
            // is the only colour a native placeholder can be. Against the dark
            // surface a customer is invited to set two lines below, that grey is
            // unreadable. The overlay under this element is the same text in the
            // operator's own ink; `pointerEvents: 'none'` means clicking it still
            // lands in the textarea, so there is no behavioural difference.
            aria-label="Message"
            disabled={!live}
            style={{
              boxSizing: 'border-box',
              display: 'block',
              width: '100%',
              minHeight: px(TEXTAREA_MIN_H),
              margin: 0,
              // Upstream's `px-2.5 py-2` with `pr-12` to clear the send button.
              padding: `${px(TEXTAREA_PAD_Y)}px ${px(TEXTAREA_PAD_RIGHT)}px ${px(TEXTAREA_PAD_Y)}px ${px(TEXTAREA_PAD_X)}px`,
              // NO BORDER AND NO FOCUS RING, which is the port's own `border-0
              // shadow-none focus-visible:ring-0`. The card around it is the frame,
              // and the panel takes focus the moment it opens — a ring that is on
              // for the whole life of the panel is not telling anybody anything.
              border: 0,
              borderRadius: px(R_BUTTON),
              background: 'transparent',
              color: onSurface,
              fontFamily: FONT_STACK,
              fontSize: `${px(14)}px`,
              lineHeight: 1.4,
              resize: 'none',
              outline: 'none',
            }}
          />
          {text === '' && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                // The card's padding, then the textarea's own. No border to add.
                top: px(PAD) + px(TEXTAREA_PAD_Y),
                left: px(PAD) + px(TEXTAREA_PAD_X),
                fontSize: `${px(14)}px`,
                lineHeight: 1.4,
                opacity: 0.45,
                pointerEvents: 'none',
              }}
            >
              Enter your message…
            </span>
          )}
          {/* The confirmation, in the placeholder's own slot — see `sentTick`. It
              replaces nothing: the box is empty at this moment by definition, since
              a send is what emptied it. `pointerEvents: 'none'` keeps the click
              landing in the textarea underneath. */}
          <span
            aria-live="polite"
            style={{
              position: 'absolute',
              top: px(PAD) + px(TEXTAREA_PAD_Y),
              left: px(PAD) + px(TEXTAREA_PAD_X),
              fontSize: `${px(14)}px`,
              lineHeight: 1.4,
              fontWeight: 600,
              opacity: sent ? 0.7 : 0,
              pointerEvents: 'none',
              transition: still ? 'none' : 'opacity 200ms ease',
            }}
          >
            {sent ? 'Sent' : ''}
          </span>
          <button
            type="button"
            onClick={send}
            disabled={!text.trim() || !live}
            aria-label="Send message"
            style={{
              boxSizing: 'border-box',
              // Upstream's `absolute right-3 bottom-3 h-8 w-8 rounded-md`.
              position: 'absolute',
              bottom: px(SEND_INSET),
              right: px(SEND_INSET),
              appearance: 'none',
              border: 0,
              margin: 0,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: px(SEND),
              height: px(SEND),
              borderRadius: px(R_BUTTON),
              background: accent,
              color: onAccent,
              opacity: !text.trim() || !live ? 0.45 : 1,
              cursor: !text.trim() || !live ? 'default' : 'pointer',
              outline: 'none',
              WebkitTapHighlightColor: 'transparent',
              transition: still ? 'none' : 'opacity 150ms ease',
            }}
          >
            <ArrowUpGlyph size={px(16)} />
          </button>
        </div>
      </div>

      {/* The phases are drawn, not written, everywhere else in this component — and
          two of them are not drawn at all. This is the only place they are said out
          loud, and it is outside the button so the announcement is not entangled
          with the button's own name. */}
      <span aria-live="polite" style={SR_ONLY}>
        {withTakeover(control, PHASE_TEXT[phase])}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The pieces
// ---------------------------------------------------------------------------

/**
 * A ghost icon button. The hover and pressed tints are `currentColor` spans rather
 * than alphas of `onSurface` — see the header — and the focus ring is the same
 * two-tone box-shadow the launcher uses, for the same reason.
 */
function IconButton({
  px,
  still,
  pressed,
  disabled,
  accent,
  onSurface,
  onClick,
  label,
  children,
}: {
  px: (n: number) => number
  still: boolean
  pressed: boolean
  disabled: boolean
  accent: string
  onSurface: string
  onClick: () => void
  label: string
  children: ReactNode
}) {
  const [hover, setHover] = useState(false)
  const [ring, setRing] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      aria-label={label}
      title={label}
      onFocus={(e) => setRing(e.currentTarget.matches(':focus-visible'))}
      onBlur={() => setRing(false)}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      style={{
        boxSizing: 'border-box',
        position: 'relative',
        appearance: 'none',
        border: 0,
        margin: 0,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 auto',
        width: px(BTN),
        height: px(BTN),
        borderRadius: px(R_BUTTON),
        background: 'transparent',
        color: onSurface,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'default' : 'pointer',
        outline: 'none',
        WebkitTapHighlightColor: 'transparent',
        boxShadow: ring ? `0 0 0 2px ${onSurface}, 0 0 0 4px ${accent}` : 'none',
        transition: still ? 'none' : 'opacity 150ms ease, box-shadow 150ms ease',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          background: 'currentColor',
          opacity: pressed ? PRESSED_TINT : hover && !disabled ? TRAY_TINT : 0,
          transition: still ? 'none' : 'opacity 150ms ease',
        }}
      />
      <span style={{ position: 'relative', display: 'flex' }}>{children}</span>
    </button>
  )
}

/**
 * The failure state, which upstream does not have — see the header.
 *
 * The message is `friendlyError`'s, already written for a visitor and already short,
 * so it is allowed to wrap and is clamped by the box rather than cut by a
 * `substring` (which is how a reference implementation produced "Microphone access
 * is bloc").
 */
function ErrorBody({ message, px }: { message: string | null; px: (n: number) => number }) {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: px(10),
        width: '100%',
        minWidth: 0,
      }}
    >
      <WarningGlyph size={px(20)} />
      <span
        style={{
          fontSize: `${px(12)}px`,
          fontWeight: 400,
          lineHeight: 1.25,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
        }}
      >
        {message ?? 'The call could not be connected. Please try again.'}
      </span>
    </span>
  )
}

/**
 * The connecting/ending spinner, animated through the Web Animations API rather than
 * `@keyframes`, because keyframes need a stylesheet and this file does not have one.
 * `element.animate` is in every engine we support; where it is missing — or where the
 * visitor asked for reduced motion — the arc simply holds still, which is the correct
 * still frame rather than a blank space.
 */
function Spinner({ size, still }: { size: number; still: boolean }) {
  const ref = useRef<SVGSVGElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || still || typeof el.animate !== 'function') return
    const anim = el.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], {
      duration: 900,
      iterations: Infinity,
      easing: 'linear',
    })
    return () => anim.cancel()
  }, [still])
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
      style={{ display: 'block', transformOrigin: '50% 50%' }}
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.3}
        strokeWidth={2.5}
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * The glyphs are hand-drawn SVG rather than an icon package's components, for the
 * reason in the header: a package's components are styled by class, and there is no
 * stylesheet here to style them with. `currentColor` throughout, so each one is
 * `onAccent` on the launcher and `onSurface` in the bar without being told.
 */
/**
 * THE HANDSET, CENTRED BY HAND AND TILTED UNDER THE POINTER.
 *
 * TWO THINGS ARE DELIBERATE HERE.
 *
 * The `translate`. The path is drawn on the icon-set convention of a 24-box with a
 * 2 px margin, so its ink runs x 3→18 and y 6→21 — a bounding box whose centre is
 * (10.5, 13.5), not (12, 12). Centring the SVG therefore centred the wrong thing: at
 * 26 px the handset sat about 1.6 px low and 1.6 px left of the tile's middle, which
 * is under a millimetre and is exactly the kind of off that reads as "slightly
 * wrong" without a viewer being able to say why. The nudge puts the INK in the
 * middle, which is what the eye is measuring. It is a transform rather than a redrawn
 * path so the shape stays diff-able against the icon it came from.
 *
 * The tilt. `filter: brightness()` on the tile is the only hover feedback this button
 * had, and on a saturated accent that is nearly invisible — the launcher is also the
 * one control on the page, so its hover state is the widget's only "yes, this is a
 * button". Twelve degrees anticlockwise is the handset coming off the hook: enough to
 * be seen at 26 px, small enough that it does not read as a spin. The curve
 * overshoots slightly and settles, and it plays in reverse on the way out for free.
 * Reduced motion gets the end state without the travel, which for a rotation means no
 * rotation at all — a tilted phone is not a resting shape.
 */
function PhoneGlyph({ size, tilt, still }: { size: number; tilt: boolean; still: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      style={{
        display: 'block',
        transform: tilt && !still ? `rotate(-${TILT_DEG}deg)` : 'none',
        transformOrigin: '50% 50%',
        transition: still ? 'none' : `transform ${ICON_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1)`,
      }}
    >
      <g transform="translate(1.5 -1.5)">
        <path d="M15.5 21A12.5 12.5 0 0 1 3 8.5 2.5 2.5 0 0 1 5.5 6h2a1.5 1.5 0 0 1 1.48 1.26l.5 2.9a1.5 1.5 0 0 1-.65 1.5l-1.1.74a10.3 10.3 0 0 0 4.1 4.1l.74-1.1a1.5 1.5 0 0 1 1.5-.65l2.9.5A1.5 1.5 0 0 1 18 16.5v2A2.5 2.5 0 0 1 15.5 21Z" />
      </g>
    </svg>
  )
}

/** A rounded square, not an X: the same "stop" the flag draws, and the shape a
 *  visitor already reads as ending something rather than dismissing it. */
function StopGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      focusable="false"
      style={{ display: 'block' }}
    >
      <rect x="5" y="5" width="14" height="14" rx="3" />
    </svg>
  )
}

function MicGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      style={{ display: 'block' }}
    >
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <path d="M12 17v4" />
    </svg>
  )
}

function MicOffGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      style={{ display: 'block' }}
    >
      <path d="M9 5a3 3 0 0 1 6 0v5" />
      <path d="M9 9v1a3 3 0 0 0 4.6 2.55" />
      <path d="M5 10a7 7 0 0 0 10.6 6" />
      <path d="M12 17v4" />
      <path d="M3 3l18 18" />
    </svg>
  )
}

function KeyboardGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      style={{ display: 'block' }}
    >
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" />
    </svg>
  )
}

function ChevronDownGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      style={{ display: 'block' }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

function ArrowUpGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      style={{ display: 'block' }}
    >
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  )
}

function WarningGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      focusable="false"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.6 3.1a1.62 1.62 0 0 1 2.8 0l8.1 14.05a1.62 1.62 0 0 1-1.4 2.43H3.9a1.62 1.62 0 0 1-1.4-2.43L10.6 3.1ZM12 8.1a.85.85 0 0 0-.85.85v4a.85.85 0 0 0 1.7 0v-4A.85.85 0 0 0 12 8.1Zm0 6.8a.95.95 0 1 0 0 1.9.95.95 0 0 0 0-1.9Z"
      />
    </svg>
  )
}

/** Visually hidden without a stylesheet — every property inline, for the reason in
 *  the header. */
const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  clipPath: 'inset(50%)',
}

/**
 * `matchMedia`, not a Tailwind variant, for the reason in the file header: inside
 * the embed's shadow root there is no compiled stylesheet for `sm:` to live in.
 *
 * The listener is also the honest way to do it even in the panel — the query is
 * about the VIEWPORT, and this component has no idea how wide its own container is.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(query)
    setMatches(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return matches
}
